// Mục "Giới thiệu của tôi" trên cổng khách /booking/me (spec 22/08/2026 §9.3).
// Dùng chung cho khách thường (nhóm customer) lẫn KOL/đối tác có SĐT trùng
// phiên — máy chủ tự chọn đúng mức hoa hồng theo nhóm, FE chỉ hiện.
//
// Mọi con số ở đây là số MÁY CHỦ trả (cùng câu lệnh với bảng đối soát nội
// bộ). FE không tự cộng lại: hai nơi cộng theo hai cách là hai con số khác
// nhau, và khách sẽ tin con số nhỏ hơn là mình bị ăn bớt.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { formatDate, formatVND } from "@/lib/format";
import { shareLink } from "@/features/affiliates/lib";
import { openChatBubble } from "@/features/customer-chat/chatBubbleBus";
import { usePublicBranches } from "./branches";
import {
  ReferralApiError,
  fetchMyReferral,
  issueMyReferralCode,
  updateMyReferralPayout,
  type PublicReferral,
  type PublicReferralCommission,
  type PublicReferralPayout,
} from "./referral-api";

const KEY = ["customer-referral", "me"] as const;

const CARD_CLASS = "space-y-4 rounded-3xl border bg-surface p-5";

/** Nhãn cho khách xem, KHÁC nhãn kế toán ("Đã chi"/"Đã giao quà"). */
const AWARD_LABEL: Record<string, string> = {
  pending: "Chờ Gạo Nâu chi",
  paid: "Đã nhận tiền",
  delivered: "Đã nhận quà",
};

export function ReferralCard() {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: KEY,
    queryFn: ({ signal }) => fetchMyReferral({ signal }),
    retry: false,
  });
  const issueM = useMutation({
    mutationFn: issueMyReferralCode,
    onSuccess: (data) => queryClient.setQueryData(KEY, data),
  });

  if (q.isLoading) return null;

  if (q.isError) {
    // 401 = chưa vào phiên khách; trang cha đã lo chuyện đăng nhập nên ở đây
    // im lặng. Lỗi khác chỉ một dòng — không được chặn danh sách booking.
    if (q.error instanceof ReferralApiError && q.error.status === 401) return null;
    return (
      <p className="mt-6 text-xs text-muted-foreground">
        Không tải được mục giới thiệu — thử lại sau.
      </p>
    );
  }

  const data = q.data as PublicReferral;

  if (!data.partner) {
    // Chưa đủ điều kiện thì ẩn HẲN: mời bấm rồi báo "bạn chưa được" là cách
    // chắc chắn nhất để khách nghĩ hệ thống hỏng.
    if (!data.canIssue) return null;
    return (
      <section className={`${CARD_CLASS} mt-6`}>
        <h2 className="text-lg font-semibold">
          Giới thiệu bạn bè — nhận hoa hồng {data.config.commissionPercent}%
        </h2>
        <p className="text-sm text-muted-foreground">
          Bạn bè dùng mã của bạn được giảm {data.config.discountPercent}%; bạn nhận{" "}
          {data.config.commissionPercent}% tiền gói khi bạn bè chụp xong và thanh toán đủ.
        </p>
        <Button onClick={() => issueM.mutate()} disabled={issueM.isPending}>
          Nhận mã giới thiệu
        </Button>
        {issueM.isError && (
          <p className="text-xs text-danger-text">
            {issueM.error instanceof Error ? issueM.error.message : "Chưa nhận được mã"}
          </p>
        )}
      </section>
    );
  }

  const partner = data.partner;
  const link = shareLink(window.location.origin, partner.code);
  // Cùng luật với máy chủ (`status IN ('eligible','paid')`): đơn chưa thu đủ
  // tiền không được kể công, nếu không khách đếm ra một đằng, máy một nẻo.
  const reached = data.commissions.filter(
    (c) => c.status === "eligible" || c.status === "paid",
  ).length;

  return (
    <section className={`${CARD_CLASS} mt-6`}>
      <h2 className="text-lg font-semibold">Giới thiệu của tôi</h2>

      <CodeBlock code={partner.code} link={link} />

      <ul className="space-y-1 text-sm text-muted-foreground">
        <li>Hoa hồng của bạn: {data.config.commissionPercent}%</li>
        <li>Bạn bè được giảm: {data.config.discountPercent}%</li>
        <li>Tính trên tiền gói sau giảm, chưa VAT</li>
        <li>Tính khi khách chụp xong và thanh toán đủ</li>
        {data.config.payoutSchedule === "monthly_bank_transfer" && (
          <li>Chi chuyển khoản, mỗi tháng một lần</li>
        )}
      </ul>

      <StatsBlock stats={data.stats} />
      <CommissionList rows={data.commissions} />
      <MilestoneList config={data.config} awards={data.awards} reached={reached} />
      <PayoutBlock payout={partner.payout} />

      <Button variant="outline" onClick={() => openChatBubble("affiliate")}>
        Nhắn cho Gạo Nâu
      </Button>

      <p className="text-xs text-muted-foreground">
        Hoa hồng chỉ tính khi đơn của người được giới thiệu thu đủ; dùng mã cho chính mình không
        được tính.
      </p>
    </section>
  );
}

function CodeBlock({ code, link }: { code: string; link: string }) {
  const [copied, setCopied] = useState(false);
  // Trình duyệt chặn clipboard (thường do không https) — vẫn phải đưa link ra
  // để khách tự bôi đen chép tay, không được im lặng coi như đã chép.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <div className="space-y-2">
      <p className="font-mono text-lg">{code}</p>
      <p className="break-all text-xs text-muted-foreground">{link}</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => void copy()}>
          {copied ? "Đã chép ✓" : "Sao chép link"}
        </Button>
        {canShare && (
          <Button
            variant="outline"
            onClick={() => void navigator.share({ title: "Gạo Nâu Profile", url: link })}
          >
            Chia sẻ
          </Button>
        )}
      </div>
    </div>
  );
}

function StatsBlock({ stats }: { stats: PublicReferral["stats"] }) {
  const cells = [
    { label: "Đơn đã giới thiệu", value: String(stats.orderCount) },
    { label: "Đang chờ", value: String(stats.pendingCount) },
    { label: "Chờ chi", value: formatVND(stats.eligibleAmount) },
    { label: "Đã nhận", value: formatVND(stats.paidAmount) },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {cells.map((c) => (
        <div key={c.label} className="rounded-2xl border bg-surface p-3">
          <p className="text-xs text-muted-foreground">{c.label}</p>
          <p className="text-base font-semibold">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function CommissionList({ rows }: { rows: PublicReferralCommission[] }) {
  const { branches } = usePublicBranches();
  // branchName do máy chủ gửi kèm tra được CẢ cơ sở đã ngừng nhận đặt lịch;
  // danh sách publicBranches chỉ có cơ sở còn nhận, nên chỉ là lớp dự phòng.
  const branchLabel = (row: PublicReferralCommission) =>
    row.branchName || branches.find((b) => b.code === row.branch)?.shortLabel || row.branch;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Từng gói bạn giới thiệu được</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Chưa có ai dùng mã của bạn.</p>
      ) : (
        rows.map((c, i) => (
          <div key={`${c.orderDate}-${i}`} className="space-y-1 rounded-2xl border bg-surface p-3 text-sm">
            <p className="text-xs text-muted-foreground">
              {formatDate(c.orderDate)} · {c.product} · {branchLabel(c)}
            </p>
            <p>{c.referee}</p>
            <p className="text-muted-foreground">
              {c.status === "pending"
                ? "—"
                : `${formatVND(c.baseAmount)} × ${c.percent}% = ${formatVND(c.amount)}`}
            </p>
            <p>
              <b>{c.statusLabel}</b>
            </p>
            {c.reason && <p className="text-xs text-muted-foreground">{c.reason}</p>}
          </div>
        ))
      )}
    </div>
  );
}

function MilestoneList({
  config,
  awards,
  reached,
}: {
  config: PublicReferral["config"];
  awards: PublicReferral["awards"];
  reached: number;
}) {
  if (config.milestones.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Thưởng theo số khách</h3>
      {config.milestones.map((m) => {
        const award = awards.find((a) => a.milestoneCount === m.count);
        const remain = m.count - reached;
        return (
          <div
            key={m.count}
            className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border bg-surface p-3 text-sm"
          >
            <span>
              {m.label}
              <span className="ml-1 text-xs text-muted-foreground">
                — {m.count} khách · {m.rewardType === "cash" ? formatVND(m.amount) : "Quà"}
              </span>
            </span>
            {award ? (
              <b>{AWARD_LABEL[award.status] ?? award.status}</b>
            ) : remain > 0 ? (
              <span className="text-muted-foreground">còn {remain} khách</span>
            ) : (
              <span className="text-muted-foreground">Đã đạt — chờ Gạo Nâu ghi sổ</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PayoutBlock({ payout }: { payout: PublicReferralPayout }) {
  const queryClient = useQueryClient();
  const payoutM = useMutation({
    mutationFn: updateMyReferralPayout,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
  const [editing, setEditing] = useState(!payout.account);
  const [bank, setBank] = useState(payout.bank);
  const [account, setAccount] = useState(payout.account);
  const [holder, setHolder] = useState(payout.holder);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Tài khoản nhận hoa hồng</h3>

      {!editing ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span>
            {payout.bank} · {payout.account} · {payout.holder}
          </span>
          <Button variant="outline" onClick={() => setEditing(true)}>
            Sửa STK
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="referral-bank">Ngân hàng</Label>
            <Input id="referral-bank" value={bank} onChange={(e) => setBank(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="referral-account">Số tài khoản</Label>
            <Input
              id="referral-account"
              inputMode="numeric"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="referral-holder">Chủ tài khoản</Label>
            <Input
              id="referral-holder"
              value={holder}
              onChange={(e) => setHolder(e.target.value)}
            />
          </div>
          <Button
            disabled={payoutM.isPending}
            onClick={() =>
              payoutM.mutate({ bank, account, holder }, { onSuccess: () => setEditing(false) })
            }
          >
            Lưu STK
          </Button>
          {payoutM.isError && (
            <p className="text-xs text-danger-text">
              {payoutM.error instanceof Error ? payoutM.error.message : "Không lưu được STK"}
            </p>
          )}
        </div>
      )}

      {payoutM.isSuccess && !editing && (
        <p className="text-xs text-muted-foreground">Đã lưu — kế toán Gạo Nâu đã được báo.</p>
      )}
    </div>
  );
}
